import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { S3Client, GetObjectCommand, ListObjectsV2Command } from "@aws-sdk/client-s3";
import HelperFunctions from "../../mango/js/common/HelperFunctions";

/**
 * Get dynamic configuration based on environment
 * @returns {Object} Configuration object with environment-specific settings
 */
const getEnvironmentConfig = () => {
    if (HelperFunctions.isProd()) {
        return {
            cognitoClientRegion: "us-east-1",
            s3ClientRegion: "us-west-2",
            identityPoolId: "us-east-1:5c3f11e8-3a6d-46a7-b61d-569be94ce75b",
            userPoolId: "us-east-1_FDwmktIzf",
            bucketName: "lighthouse-agentic-service-sessions-608713827895"
        };
    }
    // Default to beta configuration (includes isBeta() and development)
    return {
        cognitoClientRegion: "us-west-2",
        s3ClientRegion: "us-west-2",
        identityPoolId: "us-west-2:8ba8151f-1e42-48b3-a4f9-80238bea868e",
        userPoolId: "us-west-2_2cgvBlcKb",
        bucketName: "lighthouse-agentic-service-sessions-280033535693"
    };
};

/**
 * ChatSessionClient class for browser-safe S3 operations using Cognito authentication.
 * This class handles the exchange of Cognito JWT tokens for temporary AWS credentials
 * and provides methods to read files from S3 buckets.
 *
 * Singleton pattern to prevent multiple instances.
 */
class ChatSessionClient {
    // Static property to hold the singleton instance
    static instance = null;

    constructor(auth) {
        // Prevent multiple instances
        if (ChatSessionClient.instance) {
            return ChatSessionClient.instance;
        }

        this.auth = auth;

        // Get dynamic configuration based on environment
        this.config = getEnvironmentConfig();

        // Initialize S3 client in constructor
        this.initializeS3Client();

        // Store the instance
        ChatSessionClient.instance = this;
    }

    /**
     * Static method to get or create singleton instance
     * @param {Object} auth - Cognito auth object
     * @returns {ChatSessionClient} Singleton instance
     */
    static getInstance(auth) {
        if (!ChatSessionClient.instance) {
            ChatSessionClient.instance = new ChatSessionClient(auth);
        }
        return ChatSessionClient.instance;
    }

    /**
     * Initialize the S3 client with temporary credentials from Cognito Identity Pool
     * Called once in constructor to set up s3Client
     */
    initializeS3Client() {
        try {
            // Get the ID token from the current Cognito session
            const idToken = this.auth.getSignInUserSession().getIdToken().getJwtToken();

            if (!idToken) {
                throw new Error("No valid Cognito ID token found. User may not be authenticated.");
            }

            // Exchange Cognito token for temporary AWS credentials
            const credentials = fromCognitoIdentityPool({
                identityPoolId: this.config.identityPoolId,
                clientConfig: { region: this.config.cognitoClientRegion },
                logins: {
                    [`cognito-idp.${this.config.cognitoClientRegion}.amazonaws.com/${this.config.userPoolId}`]: idToken
                }
            });

            // Create S3 client using temporary credentials
            this.s3Client = new S3Client({
                region: this.config.s3ClientRegion,
                credentials
            });

            console.log("S3 Client initialized in constructor", this.s3Client);
        } catch (error) {
            console.error("Error initializing S3 client:", error);
            throw new Error(`Failed to initialize S3 client: ${error.message}`);
        }
    }

    /**
     * Fetch a file from S3 and return its contents as text
     * @param {string} key - S3 object key (file path)
     * @returns {Promise<string>} File contents as text
     */
    async fetchFileAsText(key) {
        try {
            // Create the GetObject command
            const command = new GetObjectCommand({
                Bucket: this.config.bucketName,
                Key: key
            });

            // Execute the command
            const response = await this.s3Client.send(command);

            // Convert stream to text (browser-safe method)
            const text = await response.Body.transformToString();

            console.log(`Successfully fetched file from S3. Size: ${text.length} characters`);
            return text;
        } catch (error) {
            console.error("Error fetching file from S3:", error);

            // Provide more specific error messages
            if (error.name === "NoSuchKey") {
                throw new Error(`File not found: ${key}`);
            } else if (error.name === "NoSuchBucket") {
                throw new Error(`Bucket not found: ${this.config.bucketName}`);
            } else if (error.name === "AccessDenied") {
                throw new Error(`Access denied. Check your permissions for bucket: ${this.config.bucketName}`);
            } else {
                throw new Error(`Failed to fetch file from S3: ${error.message}`);
            }
        }
    }

    /**
     * Fetch a file from S3 and return its contents as JSON
     * @param {string} key - S3 object key (file path)
     * @returns {Promise<Object>} Parsed JSON object
     */
    async fetchFileAsJson(key) {
        try {
            const text = await this.fetchFileAsText(key);
            return JSON.parse(text);
        } catch (error) {
            if (error instanceof SyntaxError) {
                throw new Error(`Invalid JSON in file ${key}: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Generate a new session ID with timestamp for proper S3 ordering
     * @returns {string} New session ID
     */
    generateSessionId() {
        // Use reverse epoch timestamp for proper S3 ordering (newest first)
        const MAX_TIMESTAMP = 999999999999999; // Large number to ensure proper reversal
        const currentTimestamp = Date.now(); // Full millisecond timestamp
        const reverseTimestamp = MAX_TIMESTAMP - currentTimestamp;
        const uuid = crypto.randomUUID();
        return `${reverseTimestamp}_${uuid}`;
    }

    /**
     * List recent chat sessions from S3
     * @param {string} userId - userId to load session for
     * @param {number} limit - Maximum number of sessions to return
     * @param {string} continuationToken - Token for pagination
     * @returns {Promise<Object>} Object with sessions array and nextToken
     */
    async listSessions(userId, limit = 20, continuationToken = null) {
        try {
            const command = new ListObjectsV2Command({
                Bucket: this.config.bucketName,
                Prefix: `${userId}/`,
                Delimiter: "/",
                MaxKeys: limit,
                ContinuationToken: continuationToken
            });

            const response = await this.s3Client.send(command);

            // Extract session IDs from common prefixes (directories)
            const sessionDirectories = (response.CommonPrefixes || [])
                .map((prefix) => {
                    // Extract session ID from prefix like "userId/session_12345_uuid/"
                    const match = prefix.Prefix.match(new RegExp(`${userId}/session_([^/]+)/`));
                    return match ? match[1] : null;
                })
                .filter(sessionId => sessionId !== null);

            // Sort lexicographically (which works with timestamp-based session IDs)
            sessionDirectories.sort();

            // Return sessions with just sessionId for immediate display
            // The UI will handle loading metadata asynchronously
            const sessions = sessionDirectories.map(sessionId => ({
                sessionId,
                firstMessage: "Loading..."
            }));

            return {
                sessions,
                hasMore: response.IsTruncated || false,
                nextToken: response.NextContinuationToken || null
            };
        } catch (error) {
            console.error("Error listing sessions:", error);
            throw new Error(`Failed to list sessions: ${error.message}`);
        }
    }

    /**
     * Load messages from a session file in S3
     * @param {string} userId - ID of user whose session we are fetching
     * @param {string} sessionId - Session ID
     * @returns {Promise<Array>} Array of messages
     */
    async loadSession(userId, sessionId) {
        try {
            console.log(`Loading session: ${userId}/session_${sessionId}`);

            // List all message files for this session
            const messagesPrefix = `${userId}/session_${sessionId}/agents/agent_default/messages/`;
            const listParams = {
                Bucket: this.config.bucketName,
                Prefix: messagesPrefix
            };

            const command = new ListObjectsV2Command(listParams);
            const listResult = await this.s3Client.send(command);
            console.log("Found message files:", listResult.Contents?.length || 0);

            if (!listResult.Contents || listResult.Contents.length === 0) {
                throw new Error(`Session '${userId}/session_${sessionId}' does not exist`);
            }

            // Sort by message number (extract from filename)
            const sortedFiles = listResult.Contents
                .filter(obj => obj.Key.endsWith(".json"))
                .sort((a, b) => {
                    const aNum = parseInt(a.Key.match(/message_(\d+)\.json$/)?.[1] || "0", 10);
                    const bNum = parseInt(b.Key.match(/message_(\d+)\.json$/)?.[1] || "0", 10);
                    return aNum - bNum;
                });

            // Load all messages
            const messages = [];
            // eslint-disable-next-line no-restricted-syntax
            for (const file of sortedFiles) {
                try {
                    const messageCommand = new GetObjectCommand({
                        Bucket: this.config.bucketName,
                        Key: file.Key
                    });

                    // eslint-disable-next-line no-await-in-loop
                    const messageResult = await this.s3Client.send(messageCommand);
                    // eslint-disable-next-line no-await-in-loop
                    const messageText = await messageResult.Body.transformToString();
                    const messageData = JSON.parse(messageText);

                    // Convert to our expected format
                    const message = {
                        // Handle tool result messages which strands by default classifies as user messages
                        role: messageData.message.content[0].toolResult ? "assistant" : messageData.message.role,
                        content: messageData.message.content
                    };

                    messages.push(message);
                } catch (error) {
                    console.error("Error loading message:", file.Key, error);
                }
            }

            return messages;
        } catch (error) {
            console.error("Error loading session:", error);
            if (error.message.includes("File not found")) {
                return []; // Return empty array for new sessions
            }
            throw error;
        }
    }

    /**
     * Get session metadata without loading full messages
     * @param {string} userId - user ID
     * @param {string} sessionId - Session ID
     * @returns {Promise<Object>} Session metadata
     */
    async getSessionMetadata(userId, sessionId) {
        try {
            // Try to fetch the first message document to get the session title
            const firstMessageKey = `${userId}/session_${sessionId}/agents/agent_default/messages/message_0.json`;
            let firstMessage = "New conversation";
            const messageCount = 0;

            try {
                const firstMessageData = await this.fetchFileAsJson(firstMessageKey);

                // Extract the user's text from the first message
                if (firstMessageData && firstMessageData.message && firstMessageData.message.content) {
                    const { content } = firstMessageData.message;
                    let userText = "";

                    // Handle different content formats
                    if (Array.isArray(content)) {
                        // Find text content in array format
                        const textContent = content.find(item => item.text);
                        userText = textContent ? textContent.text : "";
                    } else if (typeof content === "string") {
                        userText = content;
                    }

                    if (userText && userText.trim()) {
                        // Extract first sentence (up to first period, question mark, or exclamation)
                        const sentences = userText.trim().split(/[.!?]+/);
                        firstMessage = sentences[0].trim();

                        // If the first sentence is too long, truncate it
                        if (firstMessage.length > 156) {
                            firstMessage = `${firstMessage.substring(0, 153)}...`;
                        }
                    }
                }
            } catch (messageError) {
                // Keep default values
            }

            // Try to get session metadata file for timestamps
            let createdAt = new Date().toISOString();
            let updatedAt = new Date().toISOString();

            try {
                const sessionKey = `${userId}/session_${sessionId}/session.json`;
                const sessionData = await this.fetchFileAsJson(sessionKey);
                createdAt = sessionData.createdAt || createdAt;
                updatedAt = sessionData.updatedAt || updatedAt;
            } catch (sessionError) {
                // Use current timestamp if session file doesn't exist
            }

            return {
                sessionId,
                messageCount,
                firstMessage,
                createdAt,
                updatedAt
            };
        } catch (error) {
            return {
                sessionId,
                messageCount: 0,
                firstMessage: "New conversation",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
        }
    }
}

export default ChatSessionClient;
